import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, Button, InputAdornment, Tooltip } from '@mui/material';
import Icon from "@mui/material/Icon";
// icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RemoveRedEyeRounded } from "@mui/icons-material";
import AngleRightIcon from "common/customIcons/angle_right";
import SearchIcon from "common/customIcons/search";
// utils and helpers
import { formatTimestampToWithoutTime } from "utilities/common";
import { debounce } from "utilities/common";
import { getAssignedProducts, getTemplateKitDetails } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from "../../../../redux/slices/common";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import MDButton from 'components/MDButton';
import { SubCategoryFilter, BrandFilter, BoxFilter } from "common/filters";
import PlannerNotes from "common/CommonDrawer/hyperwolf/plannerNotes";
import { Transition } from "components/distributionSetting/distributionManagement/distributionDetails";
import InfiniteScrollTable from "components/tables/infiniteScrollTable";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";

const ViewTemplate = () => {
    const { templateId } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [mainDetails, setMainDetails] = useState({});
    const [details, setDetails] = useState({});
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'skuList'
    const [refreshTable, setRefreshTable] = useState(false);
    const [customFilters, setCustomFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showPlannerNotes, setShowPlannerNotes] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState({});

    const columnDefs = [
        {
            headerName: 'Box Name',
            field: 'name',
            suppressMenu: false,
            flex: 2,
            sortable: false,
            valueGetter: (params) => params.data?.boxes?.name
        },
        {
            headerName: 'Min Products Value For This Box',
            field: 'minProductValuePerBox',
            suppressMenu: false,
            sortable: false,
            flex: 1.5,
        },
        {
            headerName: 'Max Products Value For This Box',
            field: 'maxProductValuePerBox',
            suppressMenu: false,
            sortable: false,
            flex: 1.5,
        },
    ];

    const expandedColumnDefs = [
        {
            headerName: 'Product Name/SKU',
            field: 'adminProductName',
            key: 'adminProductName',
            suppressMenu: false,
            flex: 2,
            sortable: false,
            cellRenderer: (params) => {
                if (!params?.data) return "-";
                const { adminProductName, sku, assets } = params?.data || {}
                const { thumbURL, name } = assets?.[0] || {}

                return <>
                    <Box style={{ display: "flex", gap: "10px" }}>
                        <img src={thumbURL} alt={name} width={32} height={32} />
                        <Box>
                            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{adminProductName}</Typography>
                            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
                        </Box>
                    </Box>
                </>
            }
        },
        {
            headerName: 'Category',
            field: 'category',
            suppressMenu: false,
            sortable: false,
            flex: 1.5,
            cellRenderer: (params) => {
                if (!params?.data) return "-";
                const { category } = params?.data || {}

                return <Box>
                    <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{category?.name}</Typography>
                </Box>
            }
        },
        {
            headerName: 'Brand',
            field: 'brand',
            suppressMenu: false,
            sortable: false,
            flex: 1.5,
            cellRenderer: (params) => {
                if (!params?.data) return "-";
                const { brand } = params?.data || {}

                return <Box>
                    <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{brand?.name}</Typography>
                </Box>
            }
        },
        {
            headerName: 'Quantity',
            field: 'quantity',
            suppressMenu: false,
            sortable: false,
            flex: 1,
        },
        {
            headerName: 'Price',
            field: 'pricePerPiece',
            suppressMenu: false,
            sortable: false,
            flex: 1,
            valueGetter: (params) => {
                return `$${params?.data?.pricePerPiece}`
            }
        },
        {
            headerName: 'Min Qty',
            field: 'minQuantity',
            suppressMenu: false,
            sortable: false,
            flex: 1,
            valueGetter: ({ data }) => data?.minQuantity?.toString() || "-"
        },
        {
            headerName: 'Max Qty',
            field: 'maxQuantity',
            suppressMenu: false,
            sortable: false,
            flex: 1,
            valueGetter: ({ data }) => data?.maxQuantity?.toString() || "-"
        },
    ];

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handleTemplateKitDetails = async () => {
        try {
            const response = await dispatch(getTemplateKitDetails({ templateId })).unwrap()
            if (response?.data) {
                setDetails(response?.data)
                setMainDetails(response?.data)
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || error
            dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
        }
    };

    const debounceSearch = useCallback(debounce((event) => {
        setCustomFilters(prev => ({ ...prev, search: event.target.value }));
        setRefreshTable(prev => !prev)
    }, 1000), []);

    const handleChange = (e) => {
        const { value, name } = e.target;

        setCustomFilters(prev => ({ ...prev, [name]: value }));
        setRefreshTable(prev => !prev)
    };

    useEffect(() => {
        if (templateId) {
            handleTemplateKitDetails()
        }
    }, [templateId]);


    let localUser = getData("login-user-info");
    if (localUser) {
        localUser = JSON.parse(localUser);
    }

    const adminMode = localStorage.getItem("adminsMode")
        ? JSON.parse(localStorage.getItem("adminsMode"))
        : null;
    const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
    const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
    const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");

    return (
        <CommonLayout>
            <Box p={{ xs: 2, sm: 3, lg: 4 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    gap={2}
                    mb={2}
                >
                    <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
                        <IconButton onClick={() => navigate("/hyperwolf/kit-template")}>
                            <AngleRightIcon fontSize="14px" />
                        </IconButton>
                        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                            Kit Template
                        </Typography>
                    </Box>
                    {
                        canEdit &&
                        <MDButton
                            type="button"
                            variant="outlined"
                            color="dark"
                            size="small"
                            onClick={() => navigate(`/hyperwolf/kit-template/update-template/${templateId}`)}
                        >
                            <Icon>edit</Icon>&nbsp;edit
                        </MDButton>
                    }
                </Stack>
                {
                    canView &&
                    <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
                        <Grid container spacing={3}>

                            {/* Tabs */}
                            <Grid item xs={12}>
                                <Typography variant="h5" fontWeight="semibold" mb={2}>
                                    {details?.templateId}
                                </Typography>
                                <Grid container spacing={3}>
                                    <Box pt={{ xs: 2, sm: 3, lg: 4 }} pl={{ xs: 2, md: 3 }} pr={{ xs: 2, md: 3 }} className="cannabinoids-tabs white-bg-tabs">
                                        <Stack direction='row' gap={1}>
                                            <Button
                                                variant='outlined'
                                                className={activeTab === 'overview' ? 'active' : ''}
                                                sx={{ color: '#2E2E3A' }}
                                                onClick={() => handleTabChange('overview')}
                                            >
                                                Overview
                                            </Button>
                                            <Button
                                                variant='outlined'
                                                className={activeTab === 'skuList' ? 'active' : ''}
                                                sx={{ color: '#2E2E3A' }}
                                                onClick={() => handleTabChange('skuList')}
                                            >
                                                SKU List
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                            {
                                activeTab === 'overview'
                                    ? <>
                                        {/* Basic details */}
                                        <Grid item xs={12}>
                                            <Card sx={{ p: { xs: 1, md: 1 }, pb: { md: 0 }, borderRadius: 2.5, boxShadow: "none" }}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={3}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Creation Date</Typography>
                                                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{formatTimestampToWithoutTime(details.createdDate)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Min Products Per Region</Typography>
                                                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{details.minProductPerRegion}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Max Products Per Region</Typography>
                                                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{details.maxProductPerRegion}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Planner Override Notes</Typography>
                                                        {
                                                            details?.plannerOverrideNotes?.length
                                                                ? <MDButton
                                                                    onClick={() => {
                                                                        setShowPlannerNotes(true)
                                                                        setSelectedTemplate(details)
                                                                    }}
                                                                    size='small'
                                                                    startIcon={<RemoveRedEyeRounded fontSize='small' />} variant='contained' color='primary' className='btn-primary-light'>
                                                                    View Notes
                                                                </MDButton>
                                                                : <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{"-"}</Typography>
                                                        }
                                                    </Grid>
                                                    <Grid item xs={12} md={3}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Region</Typography>
                                                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">{details?.region?.region}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={9}>
                                                        <Typography variant='body2' fontWeight="normal" color="#656575" mb={1}>Sub Region</Typography>
                                                        <Typography variant='h5' fontWeight="medium" color="#2E2E3A">
                                                            {(details?.subRegions?.map(subRegion => subRegion.name))?.join(", ")}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Box pt={{ md: 2 }}>
                                                    <hr className='my-3' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
                                                </Box>
                                            </Card>
                                        </Grid>

                                        {/* Boxes listing */}
                                        <Grid item xs={12} className="table-border-gray last-child-left">
                                            <Card sx={{ p: { xs: 1, md: 1 }, pt: { md: 0 }, borderRadius: 2.5, boxShadow: "none" }}>
                                                <Typography variant='h6' color="#2E2E3A" mb={1}>
                                                    Boxes For Specific Limit
                                                    <Tooltip arrow title={"Shows the minimum and maximum quantity of products in a box."}>
                                                        <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                                                            <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                                                        </span>
                                                    </Tooltip>
                                                </Typography>
                                                <div className="ag-theme-quartz table-nodata-size" style={{ height: "56vh", width: "100%" }}>
                                                    <AgGridReact
                                                        className="ag-grid--table"
                                                        pagination={false}
                                                        rowData={details.boxes || []}
                                                        noRowsOverlayComponent={NoDataFound}
                                                        overlayLoadingTemplate={'<span class="table-loader"></span>'}
                                                        columnDefs={columnDefs}
                                                        rowModelType={"clientSide"}
                                                        headerHeight={36}
                                                        rowHeight={56}
                                                        rowSelection="multiple"
                                                        suppressRowClickSelection={true}
                                                        defaultColDef={{ ...defaultColDef }}
                                                    />
                                                </div>
                                            </Card>
                                        </Grid>
                                    </>
                                    : <Grid item xs={12}>
                                        <Card sx={{ p: { xs: 1, md: 1 }, pt: { md: 0 }, borderRadius: 2.5, boxShadow: "none" }}>
                                            {/* Search filter */}
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                alignItems={{ sm: "center" }}
                                                justifyContent="space-between"
                                                gap={2}
                                                my={2}
                                            >
                                                <Typography variant='h6' color="#2E2E3A" fontWeight="semibold">SKU List</Typography>
                                                <Box className="search-gray">
                                                    <TextField
                                                        className="custom-search-field"
                                                        placeholder="Search"
                                                        value={searchTerm || ""}
                                                        onChange={e => {
                                                            const { value } = e.target;
                                                            setSearchTerm(value);
                                                            debounceSearch(e);
                                                        }}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">
                                                                <IconButton size='small'>
                                                                    <SearchIcon width='18px' height='18px' />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }}
                                                        sx={{ minWidth: "300px" }}
                                                    />
                                                </Box>
                                            </Stack>

                                            {/* Filters */}
                                            <Box className='cus-filter-sec filter-gray-bg' mb={2}>
                                                <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                                                    <Grid item xs={12} lg={3} xxxl={2}>
                                                        <BoxFilter
                                                            value={customFilters?.boxId}
                                                            handleChange={handleChange}
                                                            placeholder={"Box Type"}
                                                            selectAllLabel={"All Boxes"}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} lg={3} xxxl={2}>
                                                        <SubCategoryFilter
                                                            value={customFilters?.categoryId}
                                                            handleChange={handleChange}
                                                            placeholder={"Category"}
                                                            selectAllLabel={"All Categories"}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} lg={3} xxxl={2}>
                                                        <BrandFilter
                                                            value={customFilters?.brandId}
                                                            handleChange={handleChange}
                                                            placeholder={"Brands"}
                                                            selectAllLabel={"All"}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            <Box className='cus-acc-white last-child-left'>
                                                <InfiniteScrollTable
                                                    tableName="Box Products"
                                                    getDataMethod={getAssignedProducts}
                                                    columnDefs={expandedColumnDefs}
                                                    customFilters={{ ...customFilters, templateId }}
                                                    refreshTable={refreshTable}
                                                    tableHeight="62vh"
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>
                            }
                        </Grid>
                    </Card>
                }
            </Box>
            {/* Planner override notes */}
            {
                canView &&
                <PlannerNotes
                    open={showPlannerNotes}
                    TransitionComponent={Transition}
                    handleClose={() => setShowPlannerNotes(false)}
                    templateData={selectedTemplate}
                />
            }
        </CommonLayout>
    )
}

export default ViewTemplate
